import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken, CertificationItem, Filter, UnitMeasure } from '@mitsubishi/shared/data-access';

import { ProjectStateUpdateEvent } from '../enums';
import {
  ActivityLog,
  EditProjectCompaniesRequest,
  ExportProjectsDataRequest,
  ExportSelectionHistory,
  HighlightedProject,
  PaginatedProjects,
  Project,
  ProjectDetails,
  ProjectFilters,
  ProjectListRequest,
} from '../models';
import { DatasheetSettings } from '../models/datasheet-settings.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private readonly baseUrl: string;
  private readonly exportSelectionUrl: string;
  private readonly projectsBaseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/project';
    this.exportSelectionUrl = apiServerUrl + '/export-selection';
    this.projectsBaseUrl = apiServerUrl + '/projects';
  }

  getProject(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.baseUrl}/${id}`);
  }

  getPrivateProjects(): Observable<ProjectDetails[] | null> {
    return this.http.get<ProjectDetails[] | null>(`${this.baseUrl}/private`);
  }

  getHighlightedProjects(): Observable<HighlightedProject[] | null> {
    return this.http.get<HighlightedProject[] | null>(`${this.baseUrl}/highlighted`);
  }

  createProject(organizationId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}`, { organizationId });
  }

  updateProjectState(projectId: string, status: ProjectStateUpdateEvent): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${projectId}/state`, {
      event: status,
    });
  }

  addFavoriteProject(id: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${id}/add-favorite`, {});
  }

  removeFavoriteProject(id: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${id}/remove-favorite`, {});
  }

  updateProjectTitle(id: string, title: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}`, { title });
  }

  updateProjectFeature(id: string, elcaFeature: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}`, { elcaFeature });
  }

  deleteProject(id: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${id}`);
  }

  duplicateProject(id: string) {
    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${id}/duplicate`, {});
  }

  editProjectCompanies(id: string, request: EditProjectCompaniesRequest) {
    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${id}/companies`, request);
  }

  updateProjectNotes(id: string, notes: string) {
    return this.http.patch<{ uuid: string }>(`${this.baseUrl}/${id}/update-note`, { note: notes });
  }

  getPaginatedProjectsWithFilters(request: ProjectListRequest): Observable<PaginatedProjects> {
    return this.http.post<PaginatedProjects>(`${this.projectsBaseUrl}`, request);
  }

  getProjectAvailableFilters(request: ProjectFilters): Observable<Filter[]> {
    return this.http.post<Filter[]>(`${this.baseUrl}/filters`, request);
  }

  updateCertification(id: string, certifications: CertificationItem[]) {
    return this.http.post<null>(`${this.baseUrl}/${id}/certification`, certifications);
  }

  getUnitsOfMisures(id: string, system?: number): Observable<UnitMeasure[]> {
    let params = new HttpParams();
    if (system) {
      params = params.append('system', system);
    }
    return this.http.get<UnitMeasure[]>(`${this.baseUrl}/${id}/uom`, { params });
  }

  updateUnitsOfMisures(id: string, uom: any) {
    return this.http.post<null>(`${this.baseUrl}/${id}/uom`, uom);
  }

  getActivityLogs(id: string): Observable<ActivityLog[]> {
    return this.http.get<ActivityLog[]>(`${this.baseUrl}/${id}/activity-logs`);
  }

  checkEnvironment(id: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${id}/check-environment`, {});
  }

  datasheetVisibility(projectId: string, unitId: string): Observable<DatasheetSettings[]> {
    return this.http.get<DatasheetSettings[]>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/datasheet/visibility`
    );
  }

  updateIsImportantFlag(
    projectId: string,
    selectionId: string,
    alternativeId: string,
    request: {
      important: boolean;
      primaryKey: string;
      secondaryKey?: string;
      referenceType: 'UNIT_ALTERNATIVE' | 'UNIT_CUSTOM_ALTERNATIVE' | 'ACCESSORY' | 'ACCESSORY_CUSTOM' | 'WARRANTY';
    }
  ): Observable<null> {
    return this.http.put<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/alternative/${alternativeId}/flag-is-important`,
      request
    );
  }

  datasheetPerformanceVisibility(
    projectId: string,
    performanceId: string,
    unitId: string
  ): Observable<DatasheetSettings[]> {
    return this.http.get<DatasheetSettings[]>(
      `${this.baseUrl}/${projectId}/study/subcategory/performance/${performanceId}/unit/${unitId}/datasheet/visibility`
    );
  }

  getExportSelectionFilters(limit?: number) {
    const params = new HttpParams();
    if (limit) {
      params.append('limit', limit.toString());
    }
    return this.http.get<ExportSelectionHistory[]>(`${this.exportSelectionUrl}/filters`, { params });
  }

  exportProjectsData(data: ExportProjectsDataRequest) {
    return this.http.post(this.exportSelectionUrl, data);
  }

  /* SHARING PROJECT */
  getSharableOrganization(): Observable<{ id: number; name: string; currencyId: number }[]> {
    return this.http.get<{ id: number; name: string; currencyId: number }[]>(`${this.baseUrl}/shareable-organizations`);
  }

  saveShareOrganization(projectId: string, organizationId: string) {
    return this.http.post(`${this.baseUrl}/${projectId}/share`, { organizationId });
  }

  saveUnshareOrganization(projectId: string, organizationId: number) {
    return this.http.post(`${this.baseUrl}/${projectId}/unshare`, { organizationId });
  }

  /* ANALISI ENERGETICA */

  getPreviewChart(id: string, energyAnalysisId: string): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${id}/energyanalysis/${energyAnalysisId}/DiagrammaPreviewGiorniPotenza`,
      {}
    );
  }
}
